import { useMemo } from 'react'
import _sortBy from 'lodash/sortBy'
import _uniqBy from 'lodash/uniqBy'
import _cloneDeep from 'lodash/cloneDeep'
import { useTreeContext } from "./TreeProvider"

/** Recursively generate data for Tree from DataItem object (Site or Account) */
const convertTreeToNodes = (
  dataItem,
  parent
) => {
  let children = []
  /** UniqBy is here to prevent the case of twho children with the same ID. Should not happened in prod but present in staging env */
  children = _uniqBy((dataItem.children || []).map((s) =>
    convertTreeToNodes(
      s,
      dataItem.is_site ? parent : dataItem, // Transfer new parent only if it is an account
    ),
  ), 'id')
  const {children: parentChildren, ...parentAccount} = parent || {}
  return {
    id: dataItem.sf_id,
    internalId: dataItem.sf_id ?? 'N/A',
    disabled: dataItem.disabled,
    name: dataItem?.name ?? 'N/A',
    data: { ...dataItem, parentAccount },
    children: _sortBy(children, 'name'),
  }
}

const findParentAccount = (account, accounts) => {
  const a = account.sf_parent_id && accounts.find(_account => {
    return _account.sf_id === account.sf_parent_id
  })
  if (a) {
    return a
  }
  return accounts.map(_account => findParentAccount(account, _account.children || [])).filter(Boolean)[0]
}

const convertAccountsToTree = (accountsToConvert) => {
  if (!accountsToConvert?.length) return []
  
  let accounts = _cloneDeep(accountsToConvert)
    .filter(account => !(account.is_site === false && account.nb_children === 0))
    .filter(account => !account.has_no_production_sites)
    .sort((a, b) => (a?.hierarchie?.length || 0) - (b?.hierarchie?.length || 0))
  
  let tree = []
  let account = accounts.shift()
  while (account) {
    account = {...account, children: []}
    const parent = findParentAccount(account, tree)
    if (parent) {
      parent.children.push(account)
    } else {
      tree.push(account)
    }
    account = accounts.shift()
  }

  return tree
}

export const convertAccountsToSites = (items) => {
  return items
    .reduce((acc, item) => {
      if (item.is_site && item.sf_parent_id && items.findIndex(({sf_id}) => sf_id === item.sf_parent_id) > -1) {
        if (!acc.includes(item.sf_parent_id)) {
          return [...acc, item.sf_parent_id]
        }
        return acc
      }
      return acc
    }, [])
    .flatMap(parentId => {
      const parent = items.find(({sf_id}) => sf_id === parentId)
      const children = items.filter(({is_site, sf_parent_id}) => is_site && sf_parent_id === parentId)
      return {
        ...parent,
        children
      }
    })
}

export const getSelectedChildrenCount = (node, checkedAccounts) => {
  if (checkedAccounts && node.data.data.sf_id) {
    return checkedAccounts.filter(account =>
      account.is_site && 
      account.hierarchie.includes(node.data.data.sf_id) &&
      account.sf_id !== node.data.data.sf_id
    ).length
  }
  return 0
}

export const removeAccount = ({ account, accounts }) => {
  return accounts.filter(({hierarchie}) => !hierarchie.includes(account.sf_id))
}

export const addAccount = ({account, accounts}) => {
  if (accounts.findIndex(({sf_id}) => sf_id === account.sf_id) === -1) {
    return [...accounts, account]
  }
  return accounts.sort((a, b) => a.hierarchie.length - b.hierarchie.length)
}

const convertAccounts = ({ accounts}) => {
  let newAccounts = convertAccountsToTree(accounts)
  let tree = newAccounts.map(account => convertTreeToNodes(account, null))
  return tree
}

export const checkInPerimeter = ({accounts}) => {
  if (!accounts || !Array.isArray(accounts)) return []
  
  let result = []
  _cloneDeep(accounts)
    .sort((a, b) => b.hierarchie.length - a.hierarchie.length)
    .forEach(account => {
      if (account.is_site || account.nb_children === 0) {
        result.push({...account, in_perimeter: true})
      } else {
        const children = result.filter(({sf_parent_id, in_perimeter}) => sf_parent_id === account.sf_id && in_perimeter)
        result.push({...account, in_perimeter: children.length === account.nb_children})
      }
    })
  return result
}

export const deserializeSelection = (accounts) => {
  if (!accounts.length) {
    return {parent_ids: [], site_ids: []}
  }
  let sortedAccounts = checkInPerimeter({accounts}).sort((a, b) => a.hierarchie.length - b.hierarchie.length)
  let parentIds = [], siteIds = []
  do {
    const account = sortedAccounts.shift()
    if (account.in_perimeter) {
      if (account.is_site) {
        siteIds.push(account.sf_id)
      } else if (account.nb_children > 0) {
        parentIds.push(account.sf_id)
      }
      sortedAccounts = sortedAccounts.filter(({hierarchie}) => !hierarchie.includes(account.sf_id))
    }
  } while (sortedAccounts.length)

  return {
    parent_ids: parentIds,
    site_ids: siteIds
  }
}

export const useInPerimeter = () => {
  const { selection } = useTreeContext()

  return useMemo(() => checkInPerimeter({ accounts: selection }).filter(({in_perimeter}) => in_perimeter), [selection])
}

export default convertAccounts
